import React from 'react'
import { useCookies } from 'react-cookie'
import { googleLogout } from '@react-oauth/google'
import { createContainer } from 'unstated-next'
import { addMilliseconds } from 'date-fns'
import { firebase } from 'firebaseApp'
import { SESSION_EXPIRY_TIME } from 'app/SessionHandler'
import { USER_TYPES } from 'constants/userRoles'
import { useLogin } from 'hooks'
import {
  AccessRequestState,
  AuthState,
  LoginQueryResult,
  SelectedSupplierState,
  SessionSate,
  UserState,
  UserType,
} from 'types/login'
import { GetCompanyResponse } from 'types/getCompany'

export type AuthContainerState = {
  authState?: AuthState
  userState?: UserState
  accessRequestState?: AccessRequestState
  selectedSupplierState?: SelectedSupplierState
}

const InitialAuthState: AuthState = {
  isAuthenticated: false,
  userType: undefined,
  accessLevels: [],
  acceptedTnC: false,
}

const InitialUserState: UserState = {
  user: null,
}

const InitialAccessRequestState: AccessRequestState = {
  requestUser: null,
  accessRequest: 'not_determined',
}

const useAuth = (initialState?: AuthContainerState) => {
  const [sessionStateInternal, setSessionStateInternal, removeSessionStateInternal] = useCookies<
    'SDHS',
    { SDHS?: SessionSate }
  >(['SDHS'])
  const [authState, setAuthState] = React.useState<AuthState>(initialState?.authState ?? InitialAuthState)
  const [userState, setUserState] = React.useState<UserState>(initialState?.userState ?? InitialUserState)
  const [accessRequestState, setAccessRequestState] = React.useState<AccessRequestState>(
    initialState?.accessRequestState ?? InitialAccessRequestState
  )
  const [selectedSupplierInternal, setSelectedSupplierInternal] = React.useState<SelectedSupplierState>(
    initialState?.selectedSupplierState ?? undefined
  )

  const { isAuthenticated, userType, accessLevels, acceptedTnC } = authState
  const { user } = userState

  const { SDHS: sessionState } = sessionStateInternal
  const setSessionState = (value: SessionSate) => {
    const sessionExpiryDate = addMilliseconds(new Date(), SESSION_EXPIRY_TIME)
    setSessionStateInternal(
      'SDHS',
      { ...sessionState, ...value },
      {
        path: '/',
        expires: sessionExpiryDate,
      }
    )
  }
  const removeSessionState = () => {
    removeSessionStateInternal('SDHS', { path: '/' })
  }

  const setSelectedSupplier = (oracleId: string, name: string, supplierDetails?: GetCompanyResponse[number]) => {
    if (oracleId && name) {
      setSelectedSupplierInternal({ oracleId, name, supplierDetails })
      setSessionState({ selectedSupplier: { oracleId, name, supplierDetails } })
    } else {
      setSelectedSupplierInternal(undefined)
      setSessionState({ selectedSupplier: undefined })
    }
  }

  const getUserType = (data: LoginQueryResult): UserType | undefined => {
    const userTypeObj = USER_TYPES.find((userType) => {
      if (
        data?.rolesAssigned?.find(
          (roleAssigned) => roleAssigned.sdhUserRoleType === `${userType.userType} - ${userType.userRoleType}`
        )
      )
        return true
      else return false
    })

    return userTypeObj?.userType
  }

  const onTnCAcceptance = () => {
    setAuthState((prev) => ({ ...prev, acceptedTnC: true }))
  }

  const onLoginSuccess = (data: LoginQueryResult) => {
    setSessionState({
      loginSession: '1',
      user: {
        googleId: data.googleId,
        morrisonMailId: data.morrisonMailId,
        familyName: data.familyName,
        givenName: data.givenName,
        userName: data.userName,
      },
    })
    setUserState({
      user: data,
    })
    const userType = getUserType(data)
    setAuthState({
      userType: userType,
      accessLevels: data?.sdhUserAccessLevels ?? [],
      acceptedTnC: data?.acceptedTermsAndConditions === 'Y',
      isAuthenticated: true,
    })

    //Set default selected supplier if user belongs to a supplier company
    if (sessionState?.selectedSupplier) {
      if (
        userType === 'Site Administrator' ||
        userType === 'Morrisons Internal' ||
        data?.supplierDetails?.oracleId === sessionState.selectedSupplier?.oracleId ||
        data?.relatedSuppliers?.some((item) => item.oracleId === sessionState.selectedSupplier?.oracleId)
      ) {
        setSelectedSupplierInternal(sessionState.selectedSupplier)
      }
    } else if (data?.company && data?.supplierDetails?.oracleId && /^\d+$/.test(data.supplierDetails.oracleId)) {
      const supplierData = {
        name: data.company,
        oracleId: data.supplierDetails.oracleId,
      }
      setSelectedSupplierInternal(supplierData)
      setSessionState({ selectedSupplier: supplierData })
    }
  }

  const onLogoutSuccess = () => {
    googleLogout()
    removeSessionState()
    setAuthState(InitialAuthState)
    setUserState(InitialUserState)
    setSelectedSupplierInternal(undefined)
  }

  const logOut = async () => {
    await firebase.auth().signOut()
  }

  const { loginStatus, loginError, mutateLogin } = useLogin({ onLoginSuccess, setAccessRequestState, logOut })

  React.useEffect(() => {
    const unregisterAuthObserver = firebase.auth().onIdTokenChanged(
      (user) => {
        if (sessionState?.loginSession === '1') {
          if (user) {
            const sessionUser = sessionState?.user
            if (sessionUser && loginStatus === 'idle') {
              mutateLogin({
                googleId: sessionUser.googleId ?? user.providerData?.[0]?.uid ?? '',
                morrisonMailId: sessionUser.morrisonMailId ?? user.email ?? '',
                familyName: sessionUser.familyName ?? '',
                givenName: sessionUser.givenName ?? '',
                userName: sessionUser.userName ?? '',
              })
            }
          } else {
            onLogoutSuccess()
          }
        }
      },
      (error) => {
        onLogoutSuccess()
        console.error('Firebase Auth Error!', error)
      }
    )

    return () => unregisterAuthObserver()
    // eslint-disable-next-line
  }, [sessionState, loginStatus])

  return {
    loginStatus,
    loginError,
    sessionState,
    accessRequestState,
    isAuthenticated,
    acceptedTnC,
    user,
    userType,
    accessLevels,
    selectedSupplier: selectedSupplierInternal,
    mutateLogin,
    logOut,
    onLoginSuccess,
    onLogoutSuccess,
    setSessionState,
    removeSessionState,
    setAccessRequestState,
    onTnCAcceptance,
    setUserState,
    setSelectedSupplier,
  }
}

export const AuthContainer = createContainer(useAuth)
